"use client";

import { useEffect, useRef, useState } from "react";

// Declare ElfsightWidget on window for TypeScript
declare global {
  interface Window {
    ElfsightWidget?: {
      init?: () => void;
    };
  }
}

const GoogleReviews: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || scriptLoaded) return;

    // Check if script already exists
    if (document.querySelector('script[src*="elfsight"]')) {
      setScriptLoaded(true);
      // Try to initialize existing widget
      setTimeout(() => {
        if (window.ElfsightWidget?.init) {
          window.ElfsightWidget.init();
        }
      }, 100);
      return;
    }

    // Load Elfsight script
    const script = document.createElement('script');
    script.src = 'https://static.elfsight.com/platform/platform.js';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      setScriptLoaded(true);
      // Initialize widget after script loads
      setTimeout(() => {
        if (window.ElfsightWidget?.init) {
          window.ElfsightWidget.init();
        }
      }, 100);
    };

    script.onerror = () => {
      console.error('Failed to load Elfsight widget script');
    };

    document.head.appendChild(script);
  }, [isVisible, scriptLoaded]);

  return (
    <section className="relative w-full bg-white py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 md:px-12 flex flex-col items-center text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-black mb-8 relative after:content-[''] after:block after:w-[200px] after:h-[3px] after:mx-auto after:mt-5 after:bg-gradient-to-r after:from-[#AE7D2F] after:via-[#ECD05B] after:to-[#AE7D2F]">
          What Our Patients Say About Smile4Me
        </h2>
        
        <div ref={containerRef} className="w-full max-w-4xl">
          {!scriptLoaded && isVisible && (
            <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-gray-600">Loading reviews...</p>
              </div>
            </div>
          )}
          
          {isVisible && (
            <div className="elfsight-app-1c158222-6500-4225-a370-afef0a31c821"></div>
          )}
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;